import * as Sentry from '@sentry/react'
import posthog from 'posthog-js'
import {redactarEventoSentry, redactarPropiedadesPosthog} from '@/lib/url-redaction'
import {anotarEvento, vaciarBitacora} from '@/lib/bitacora-sesion'

// Mirrors the features.json behavior names so PostHog funnels are the specced flows;
// `ai_validation_flagged` is emitted server-side.
export type TelemetryEvent =
  | 'risk_assigned'
  | 'employees_registered'
  | 'assessment_started'
  | 'audit_export_started'
  | 'first_soporte_uploaded'
  | 'tour_started'
  | 'tour_step_completed'
  | 'tour_completed'
  | 'tour_skipped'
  | 'item_marked'
  | 'item_unmarked'
  | 'autoevaluacion_finished'
  | 'action_created'
  | 'action_advanced'
  | 'solicitud_created'
  | 'solicitud_fulfilled'
  | 'ai_toggle_used'
  | 'ai_flagged_shown'
  | 'ai_dismissed'
  | 'member_home_viewed'
  | 'member_soporte_uploaded'
  | 'member_action_advanced'
  | 'action_note_added'
  | 'action_note_removed'
  | 'item_note_added'
  | 'item_note_removed'
  | 'action_evidence_uploaded'
  | 'action_eficacia_verified'
  | 'action_eficacia_corrected'
  | 'action_seguimiento_created'
  | 'analisis_causa_abierto'
  | 'analisis_sugerencia_descartada'
  | 'investigacion_reported'
  | 'investigacion_accion_created'
  | 'investigacion_evidence_uploaded'
  | 'entrevista_completed'
  | 'entrevista_turno_deshecho'
  | 'registration_link_regenerated'
  | 'employee_approved'
  | 'employee_rejected'
  | 're_solicitar_evaluacion'
  | 're_solicitar_todas'
  | 'registro_empresa_visto'
  | 'registro_empresa_paso1_ok'
  | 'registro_empresa_paso2_ok'
  | 'empresa_creada'
  | 'registro_link_abierto'
  | 'registro_cedula_ingresada'
  | 'registro_completado'
  | 'autoevaluacion_iniciada'
  | 'autoevaluacion_completada'
  | 'cifra_fijada'
  | 'vigencia_sve_fijada'
  // PQ-2a (E10-H01)
  | 'emparejamiento_aplicado'
  // PQ-2b (E10-H02)
  | 'perfil_cargo_borrador_guardado'
  | 'perfil_cargo_confirmado'
  // PQ-15 (E10-H18)
  | 'perfil_sugerencia_decidida'
  // PQ-14 (E1-H02)
  | 'matriz_cargo_aviso_ignorado'
  | 'matriz_cargo_aviso_vuelve'
  // PQ-2c (E10-H03)
  | 'condiciones_tanda_confirmada'
  // PQ-3 (E10-H06)
  | 'perfiles_cargo_bloque_confirmado'
  // PQ-2d (E10-H04)
  | 'pregunta_cargo_respondida'
  // PQ-6 (E1-H01/H03)
  | 'ips_registrada'
  | 'ips_declarada'
  | 'solicitudes_ips_lote'
  | 'pregunta_cargo_contradiccion_decidida'
  // PQ-7a (E1-H04)
  | 'solicitud_ips_enviada'
  // PQ-7b: cada renovación dice que el plazo del enlace de carga se quedó corto.
  | 'solicitud_ips_enlace_renovado'
  | 'carga_ips_recibida'
  | 'carga_ips_retenida'
  // PQ-9 (E10-H16)
  | 'obra_creada'
  | 'obra_peligros_confirmados'
  // PQ-10 (E10-H12)
  | 'decision_ingreso_tomada'
  // PQ-5 (E10-H15)
  | 'carpeta_visita_generada'
  | 'carpeta_visita_descargada'

// Properties are always opaque ids and codes: never cédula, NIT, names, filenames or anything
// health-adjacent.
type TelemetryProps = Record<string, string | number | boolean>

const sentryDsn = import.meta.env.VITE_SENTRY_DSN
const posthogKey = import.meta.env.VITE_POSTHOG_KEY

let posthogReady = false

// Las rutas de credenciales llevan en la URL un secreto canjeable por sesión y rrweb no enmascara URLs: estos
// ganchos son el último punto antes del transporte, y devolver `null` descarta el evento (falla cerrado).
function sanearEventoSentry<T>(evento: T): T | null {
  return redactarEventoSentry(evento) ? evento : null
}

// No-op without the DSN. No replayIntegration on purpose: PostHog already records, and Sentry replay
// would only add PII surface and cost.
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? 'local',
    integrations: [Sentry.browserTracingIntegration()],
    // Pilot-scale traffic makes full sampling cheap.
    tracesSampleRate: 1.0,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    // `request.url`, el nombre de la transacción, las migas y los spans llevan la URL completa, token incluido.
    beforeSend: sanearEventoSentry,
    beforeSendTransaction: sanearEventoSentry,
  })
}

// Replay SIN enmascarar a propósito: la ficha, la batería y el detalle del empleado viajan a PostHog tal
// como se ven. `.ph-no-capture` saca un nodo del replay; `before_send` quita las credenciales de la URL.
if (posthogKey) {
  posthog.init(posthogKey, {
    api_host: import.meta.env.VITE_POSTHOG_HOST ?? 'https://us.i.posthog.com',
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    mask_all_text: false,
    mask_all_element_attributes: false,
    disable_session_recording: false,
    session_recording: {
      maskAllInputs: false,
    },
    person_profiles: 'identified_only',
    // `$session_entry_url` se congela al arrancar la sesión y se repite en todos los eventos: limpiar la
    // barra llega tarde, y este gancho es el que cierra el hueco.
    before_send: (evento) => {
      if (evento?.properties && !redactarPropiedadesPosthog(evento.properties)) return null
      return evento
    },
  })
  posthogReady = true
}

// The only call site: components import `track`, never `posthog`, so the vocabulary stays typed.
export function track(event: TelemetryEvent, props?: TelemetryProps): void {
  anotarEvento(event)
  if (!posthogReady) return
  posthog.capture(event, props)
}

/** Only the opaque id: cédula, name and email stay out of Sentry and PostHog. */
export function identifyTelemetry(userId: string): void {
  Sentry.setUser({id: userId})
  if (posthogReady) posthog.identify(userId)
}

export function clearTelemetry(): void {
  vaciarBitacora()
  Sentry.setUser(null)
  if (posthogReady) posthog.reset()
}

export function contextoDeTelemetria(): {
  posthogSessionId: string | null
  replayUrl: string | null
  sentryEventId: string | null
} {
  const sesion = posthogReady ? posthog.get_session_id() : ''
  const replay = posthogReady ? posthog.get_session_replay_url({withTimestamp: true}) : ''
  return {
    posthogSessionId: sesion || null,
    replayUrl: replay || null,
    sentryEventId: Sentry.lastEventId() ?? null,
  }
}
